import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import type {
  ParsedCsvResult,
  IdValidationConnectionFailure,
  OrgIdValidationResult,
  MassMigrationOrgType,
  DetectedModuleDto,
  DetectedModulesResult,
} from '@/types/massMigration';

interface ModuleValidationResult {
  rowNumber: number;
  moduleKey: string;
  status: string;
  issue: string | null;
}

interface MassMigrationWizardState {
  step: number;
  orgType: MassMigrationOrgType | null;
  parsedCsv: ParsedCsvResult | null;
  idValidationResults: OrgIdValidationResult[];
  // Held alongside the results because the step re-renders from the store without
  // re-querying, so the banner explaining why rows failed has to persist with them.
  idValidationConnectionFailures: IdValidationConnectionFailure[];
  excludedRows: number[];
  selectionsByRow: Record<number, string[]>;
  detectedModulesByRow: Record<number, DetectedModuleDto[]>;
  userMigrationEnabledRows: number[];
  overridesByRow: Record<number, Record<string, Record<string, string | null>>>;
  moduleValidationResults: ModuleValidationResult[];
}

interface MassMigrationWizardActions {
  setStep: (step: number) => void;
  setOrgType: (orgType: MassMigrationOrgType) => void;
  setParsedCsv: (result: ParsedCsvResult) => void;
  setIdValidationResults: (
    results: OrgIdValidationResult[],
    connectionFailures?: IdValidationConnectionFailure[],
  ) => void;
  toggleExcluded: (rowNumber: number) => void;
  excludeAllFailed: () => void;
  setModuleSelections: (selectionsByRow: Record<number, string[]>) => void;
  setDetectedModules: (result: DetectedModulesResult) => void;
  toggleModuleForRow: (rowNumber: number, moduleKey: string) => void;
  toggleUserMigration: (rowNumber: number) => void;
  setUserMigrationForRows: (rowNumbers: number[], enabled: boolean) => void;
  setOverride: (rowNumber: number, moduleKey: string, fieldKey: string, value: string | null) => void;
  setModuleValidationResults: (results: ModuleValidationResult[]) => void;
  reset: () => void;
}

type MassMigrationWizardStore = MassMigrationWizardState & {
  actions: MassMigrationWizardActions;
};

const initialState: MassMigrationWizardState = {
  step: 0,
  orgType: null,
  parsedCsv: null,
  idValidationResults: [],
  idValidationConnectionFailures: [],
  excludedRows: [],
  selectionsByRow: {},
  detectedModulesByRow: {},
  userMigrationEnabledRows: [],
  overridesByRow: {},
  moduleValidationResults: [],
};

export const useMassMigrationWizardStore = create<MassMigrationWizardStore>()(
  devtools(
    persist(
      immer((set) => ({
        ...initialState,
        actions: {
          setStep: (step) =>
            set((state) => {
              state.step = step;
            }),

          setOrgType: (orgType) =>
            set((state) => {
              state.orgType = orgType;
            }),

          // A new file invalidates everything derived from the old one; otherwise the
          // ID validation step sees stale results and never re-queries.
          setParsedCsv: (result) =>
            set((state) => {
              Object.assign(state, initialState, {
                step: state.step,
                orgType: state.orgType,
                parsedCsv: result,
              });
            }),

          setIdValidationResults: (results, connectionFailures = []) =>
            set((state) => {
              state.idValidationResults = results;
              state.idValidationConnectionFailures = connectionFailures;
            }),

          toggleExcluded: (rowNumber) =>
            set((state) => {
              const idx = state.excludedRows.indexOf(rowNumber);
              if (idx >= 0) {
                state.excludedRows.splice(idx, 1);
              } else {
                state.excludedRows.push(rowNumber);
              }
              // Pre-flight results describe a specific set of (row, module) pairs; invalidate them.
              state.moduleValidationResults = [];
            }),

          excludeAllFailed: () =>
            set((state) => {
              const failedRows = state.idValidationResults
                .filter(
                  (r) => r.bridgeStatus === 'Invalid' || r.amplifyStatus === 'Invalid',
                )
                .map((r) => r.rowNumber);
              const combined = new Set([...state.excludedRows, ...failedRows]);
              state.excludedRows = [...combined];
              // Pre-flight results describe a specific set of (row, module) pairs; invalidate them.
              state.moduleValidationResults = [];
            }),

          setModuleSelections: (selectionsByRow) =>
            set((state) => {
              state.selectionsByRow = selectionsByRow;
              // Pre-flight results describe a specific set of (row, module) pairs; invalidate them.
              state.moduleValidationResults = [];
            }),

          setDetectedModules: (result) =>
            set((state) => {
              state.detectedModulesByRow = result.byRow;
              // Auto-select all detected module keys per row
              const selections: Record<number, string[]> = {};
              for (const [rowKey, modules] of Object.entries(result.byRow)) {
                selections[Number(rowKey)] = modules.map((m) => m.moduleKey);
              }
              state.selectionsByRow = selections;
              // Pre-flight results describe a specific set of (row, module) pairs; invalidate them.
              state.moduleValidationResults = [];
            }),

          toggleModuleForRow: (rowNumber, moduleKey) =>
            set((state) => {
              const current = state.selectionsByRow[rowNumber] ?? [];
              const idx = current.indexOf(moduleKey);
              if (idx >= 0) {
                current.splice(idx, 1);
              } else {
                current.push(moduleKey);
              }
              state.selectionsByRow[rowNumber] = current;
              // Pre-flight results describe a specific set of (row, module) pairs; invalidate them.
              state.moduleValidationResults = [];
            }),

          toggleUserMigration: (rowNumber) =>
            set((state) => {
              const idx = state.userMigrationEnabledRows.indexOf(rowNumber);
              if (idx >= 0) {
                state.userMigrationEnabledRows.splice(idx, 1);
              } else {
                state.userMigrationEnabledRows.push(rowNumber);
              }
            }),

          setUserMigrationForRows: (rowNumbers, enabled) =>
            set((state) => {
              const rest = state.userMigrationEnabledRows.filter((n) => !rowNumbers.includes(n));
              state.userMigrationEnabledRows = enabled ? [...rest, ...rowNumbers] : rest;
            }),

          setOverride: (rowNumber, moduleKey, fieldKey, value) =>
            set((state) => {
              if (!state.overridesByRow[rowNumber]) {
                state.overridesByRow[rowNumber] = {};
              }
              const moduleOverrides = state.overridesByRow[rowNumber];
              if (!moduleOverrides[moduleKey]) {
                moduleOverrides[moduleKey] = {};
              }
              moduleOverrides[moduleKey][fieldKey] = value;
            }),

          setModuleValidationResults: (results) =>
            set((state) => {
              state.moduleValidationResults = results;
            }),

          reset: () =>
            set((state) => {
              Object.assign(state, initialState);
            }),
        },
      })),
      {
        name: 'mass-migration-wizard',
        partialize: (state) => ({
          step: state.step,
          orgType: state.orgType,
          parsedCsv: state.parsedCsv,
          idValidationResults: state.idValidationResults,
          idValidationConnectionFailures: state.idValidationConnectionFailures,
          excludedRows: state.excludedRows,
          selectionsByRow: state.selectionsByRow,
          detectedModulesByRow: state.detectedModulesByRow,
          userMigrationEnabledRows: state.userMigrationEnabledRows,
          overridesByRow: state.overridesByRow,
          moduleValidationResults: state.moduleValidationResults,
        }),
      },
    ),
    { name: 'MassMigrationWizardStore' },
  ),
);

// Selectors for convenient access
export const useMassMigrationWizardStep = () =>
  useMassMigrationWizardStore((state) => state.step);
export const useMassMigrationWizardOrgType = () =>
  useMassMigrationWizardStore((state) => state.orgType);
export const useMassMigrationWizardParsedCsv = () =>
  useMassMigrationWizardStore((state) => state.parsedCsv);
export const useMassMigrationWizardIdValidation = () =>
  useMassMigrationWizardStore((state) => state.idValidationResults);
export const useMassMigrationWizardIdValidationFailures = () =>
  useMassMigrationWizardStore((state) => state.idValidationConnectionFailures);
export const useMassMigrationWizardExcludedRows = () =>
  useMassMigrationWizardStore((state) => state.excludedRows);
export const useMassMigrationWizardSelections = () =>
  useMassMigrationWizardStore((state) => state.selectionsByRow);
export const useMassMigrationWizardDetectedModules = () =>
  useMassMigrationWizardStore((state) => state.detectedModulesByRow);
export const useMassMigrationWizardUserMigrationRows = () =>
  useMassMigrationWizardStore((state) => state.userMigrationEnabledRows);
export const useMassMigrationWizardOverrides = () =>
  useMassMigrationWizardStore((state) => state.overridesByRow);
export const useMassMigrationWizardModuleValidation = () =>
  useMassMigrationWizardStore((state) => state.moduleValidationResults);
export const useMassMigrationWizardActions = () =>
  useMassMigrationWizardStore((state) => state.actions);
